import { COLLECTIONS, ROLES } from '@giftsay/constants';
import {
  DEFAULT_STORE_FEATURE_SETTINGS,
  DEFAULT_STORE_NOTIFICATION_SETTINGS,
  DEFAULT_STORE_SHIPPING_SETTINGS,
  resolveStoreFeatures,
  resolveStoreNotifications,
  resolveStoreShipping,
  STORE_STATUSES,
  type StoreFeatureSettings,
  type StoreNotificationSettings,
  type StoreShippingSettings,
  type StoreStatus,
} from '@giftsay/types';
import { doc, onSnapshot } from 'firebase/firestore';
import * as React from 'react';

import { firestore } from '../../../lib/firebase.js';
import { useAppSelector } from '../../../store/hooks.js';

/** Every admin-controlled switch on a store, as the vendor portal reads them. */
interface StoreSwitches {
  notifications: StoreNotificationSettings;
  features: StoreFeatureSettings;
  shipping: StoreShippingSettings;
  /** Platform admins can suspend a store — its portal then shows a notice instead. */
  status: StoreStatus;
}

const DEFAULT_SWITCHES: StoreSwitches = {
  notifications: DEFAULT_STORE_NOTIFICATION_SETTINGS,
  features: DEFAULT_STORE_FEATURE_SETTINGS,
  shipping: DEFAULT_STORE_SHIPPING_SETTINGS,
  status: STORE_STATUSES.ACTIVE,
};

// One listener per store shared by every component that asks (nav, route gates, bell), so the
// portal pays a single read on load plus one when an admin flips a switch, not one per caller.
let watchedStoreId: string | null = null;
let current: StoreSwitches = DEFAULT_SWITCHES;
let stopWatching: (() => void) | null = null;
const subscribers = new Set<() => void>();

function watch(storeId: string, onChange: () => void): () => void {
  if (watchedStoreId !== storeId) {
    stopWatching?.();
    watchedStoreId = storeId;
    current = DEFAULT_SWITCHES;
    stopWatching = onSnapshot(
      doc(firestore, COLLECTIONS.STORES, storeId),
      (snapshot) => {
        const store = snapshot.data();
        current = {
          notifications: resolveStoreNotifications(store),
          features: resolveStoreFeatures(store),
          shipping: resolveStoreShipping(store),
          status: (store?.['status'] as StoreStatus | undefined) ?? STORE_STATUSES.ACTIVE,
        };
        subscribers.forEach((notify) => notify());
      },
      (error) => console.warn('[store-notifications] listener stopped:', error),
    );
  }
  subscribers.add(onChange);
  return () => {
    subscribers.delete(onChange);
    if (subscribers.size === 0) {
      stopWatching?.();
      stopWatching = null;
      watchedStoreId = null;
    }
  };
}

const getCurrent = () => current;
const getDefault = () => DEFAULT_SWITCHES;
const noopSubscribe = () => () => {};

/**
 * The admin-controlled switches for the signed-in vendor's store. Platform staff are never
 * restricted by them, so they always get everything on (and no listener).
 */
function useStoreSwitches(): StoreSwitches {
  const user = useAppSelector((state) => state.auth.user);
  const storeId = user?.role === ROLES.VENDOR ? user.storeId : null;
  const subscribe = React.useCallback(
    (onChange: () => void) => (storeId ? watch(storeId, onChange) : noopSubscribe()),
    [storeId],
  );
  return React.useSyncExternalStore(subscribe, storeId ? getCurrent : getDefault);
}

/** The admin-controlled order notification switches for the signed-in vendor's store. */
export function useStoreNotifications(): StoreNotificationSettings {
  return useStoreSwitches().notifications;
}

/** The admin-controlled storefront features for the signed-in vendor's store. */
export function useStoreFeatures(): StoreFeatureSettings {
  return useStoreSwitches().features;
}

/** The signed-in vendor's own store's shipping settings (final status, delivery partners). */
export function useVendorStoreShipping(): StoreShippingSettings {
  return useStoreSwitches().shipping;
}

/** The signed-in vendor's store status — always 'active' for platform staff. */
export function useVendorStoreStatus(): StoreStatus {
  return useStoreSwitches().status;
}
